import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Socket } from "socket.io-client";
import IOnTypeaheadCatalogItemDTO from "../../../../../models/DTO/IOnTypeaheadCatalogItemDTO";
import ITypeaheadCatalogItemDTO from "../../../../../models/DTO/ITypeaheadCatalogItemDTO";
import EndpointsEnum from "../../../../../models/EndpointsEnum";
import IBomLineUI from "../../../../../models/IBomLineUI";
import ICatalog_Item from "../../../../../models/ICatalog_Item";
import IComponentShortage from "../../../../../models/IComponentShortage";
import ISupplierModel from "../../../../../models/ISupplierModel";
import IUserUI from "../../../../../models/IUserUI";
import OwLabel from "../../../../shared/OwLabel/OwLabel";
import OwPill from "../../../../shared/OwPill/OwPill";
import Typeahead from "../../../../shared/Typeahead/Typeahead";
import { ComponentShortageWarning } from "../../../../inventory/ComponentShortageWarning/ComponentShortageWarning";
import "./BomDetail.scss";

type Props = {
    /** The current lines (from the loaded item, or as edited so far). */
    lines: IBomLineUI[];
    /** Called with the full list after every user change. The server replace-sets from it. */
    updateLinesFn: (lines: IBomLineUI[]) => void;
    socket: Socket;
    catalogGuid: string;
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    /** The item being edited, so it cannot be added as its own component. Undefined on create. */
    parentCatalogItemId?: number;
    /** True while an existing item's detail is still loading: edits made then would be lost or would replace-set blindly. */
    disabled?: boolean;
    /**
     * Saved component status by component id (OR-1460), from the server's Bom_Lines `shortage`. Only present
     * for defer-to-serial suppliers; lines without an entry (unsaved, or not inventory-tracked) show no status.
     */
    shortagesByComponentId?: Record<number, IComponentShortage>;
};

/**
 * Bill of materials editor (OR-1457): the component items consumed when one unit of this catalog
 * item is built. Available to every supplier. Serialized items cannot be components (their
 * counters are serial-derived), so the typeahead will not select them.
 */
export const BomDetail: React.FC<Props> = (props) => {
    const intl = useIntl();
    const [foundItems, setFoundItems] = useState<ICatalog_Item[] | undefined>();
    const [clearTypeahead, setClearTypeahead] = useState<boolean>(false);
    const [lines, setLines] = useState<IBomLineUI[]>(props.lines || []);
    // Typeahead caches selectedItemFn / isSelectableFn in its own state at mount, so those callbacks
    // must read the latest lines and disabled state through refs rather than closing over a stale render.
    // The refs are synchronized in effects (never during render) and, for lines, also in commit() so
    // two selections in a row see each other before React re-renders.
    const linesRef = useRef<IBomLineUI[]>(lines);
    const disabledRef = useRef<boolean>(!!props.disabled);

    useEffect(() => {
        linesRef.current = lines;
    }, [lines]);

    useEffect(() => {
        disabledRef.current = !!props.disabled;
    }, [props.disabled]);
    /** Raw qty text per row while the user is typing (so the field can be cleared); committed on blur. */
    const [qtyDrafts, setQtyDrafts] = useState<Record<number, string>>({});

    useEffect(() => {
        setLines(props.lines || []);
    }, [props.lines]);

    useEffect(() => {
        const onTypeaheadCatalogItem = (data: IOnTypeaheadCatalogItemDTO) => {
            setFoundItems(data.catalog_items);
        };

        props.socket.on(EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, onTypeaheadCatalogItem);

        return () => {
            props.socket.removeListener(EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, onTypeaheadCatalogItem);
        };
    }, [props.socket]);

    const commit = (nextLines: IBomLineUI[]) => {
        linesRef.current = nextLines;
        setLines(nextLines);
        props.updateLinesFn(nextLines);
    };

    const typeaheadCatalogItem = (name: string) => {
        const payload: ITypeaheadCatalogItemDTO = {
            catalogGuid: props.catalogGuid,
            userGuid: props.userModel.guid,
            userToken: props.userModel.token,
            supplierGuid: props.supplierModel.guid,
            name,
            onlyActive: true,
            options: {
                searchName: true,
                searchDescription: true,
                searchSku: true,
                searchUpc: true,
            },
            skipAlternateSourceFiltering: true,
            includeComponents: true,
        };

        props.socket.emit(EndpointsEnum.CatalogItemTypeaheadCatalogItem, payload);
    };

    const isAlreadyAdded = (item: ICatalog_Item) => linesRef.current.some((line) => line.component_catalog_item_id === item.id);

    const isSelectable = (item: ICatalog_Item): boolean => {
        if (disabledRef.current || !item || item.has_serials) {
            return false;
        }

        if (props.parentCatalogItemId && item.id === props.parentCatalogItemId) {
            return false;
        }

        return !isAlreadyAdded(item);
    };

    const selectedItemFn = (item: ICatalog_Item) => {
        if (!isSelectable(item)) {
            return;
        }

        const newLine: IBomLineUI = {
            component_catalog_item_id: item.id,
            qty_per_unit: 1,
            Component: item,
        };

        commit([...linesRef.current, newLine]);
        setClearTypeahead(true);
    };

    const clearTypeaheadFn = () => {
        setClearTypeahead(false);
    };

    /** Removing works for saved and unsaved rows alike: the server replace-sets from the full list. */
    const removeLine = (index: number) => {
        if (disabledRef.current) {
            return;
        }

        const nextLines = [...lines];

        nextLines.splice(index, 1);
        setQtyDrafts({});
        commit(nextLines);
    };

    const setLineQty = (index: number, qty: number) => {
        const nextLines = lines.map((line, lineIndex) => (lineIndex === index ? { ...line, qty_per_unit: qty } : line));

        commit(nextLines);
    };

    /** Commit whole numbers >= 1 as they are typed; keep anything else (e.g. an emptied field) as a draft until blur. */
    const updateQty = (index: number, e: ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        const parsed = Number(raw);

        if (raw.trim() !== "" && Number.isInteger(parsed) && parsed >= 1) {
            setQtyDrafts((drafts) => {
                const next = { ...drafts };

                delete next[index];

                return next;
            });
            setLineQty(index, parsed);
        } else {
            setQtyDrafts((drafts) => ({ ...drafts, [index]: raw }));
        }
    };

    /** On blur, an empty or invalid draft falls back to 1 (the server requires an integer >= 1). */
    const commitQtyDraft = (index: number) => {
        if (!(index in qtyDrafts)) {
            return;
        }

        const parsed = Number(qtyDrafts[index]);
        const qty = Number.isInteger(parsed) && parsed >= 1 ? parsed : 1;

        setQtyDrafts((drafts) => {
            const next = { ...drafts };

            delete next[index];

            return next;
        });
        setLineQty(index, qty);
    };

    /** OK, or short by N with the demand breakdown (OR-1460). */
    const renderStatus = (componentId: number) => {
        const shortage = props.shortagesByComponentId?.[componentId];

        if (!shortage) {
            return null;
        }

        if (shortage.shortfall > 0) {
            return (
                <span className={"text-warning text-small ms-2 no-wrap"} data-testid="bom-line-status">
                    {intl.formatMessage({ id: "short_by_n", defaultMessage: "Short by {qty}" }, { qty: shortage.shortfall })}
                    <ComponentShortageWarning shortage={shortage} />
                </span>
            );
        }

        return (
            <span className={"text-success text-small ms-2"} data-testid="bom-line-status">
                {intl.formatMessage({ id: "ok", defaultMessage: "Ok" })}
            </span>
        );
    };

    const renderLookupItem = (item: ICatalog_Item) => {
        const unselectable = !isSelectable(item);

        return (
            <div className={unselectable ? "text-muted" : ""}>
                <div>
                    {item.name}
                    {item.has_serials ? <OwPill title={"serialized"} className={"ms-2"} /> : null}
                    {isAlreadyAdded(item) ? <OwPill title={"added"} className={"ms-2"} /> : null}
                </div>
                <div className={"text-muted text-small"}>{item.sku}</div>
            </div>
        );
    };

    return (
        <div className={"BomDetail"} data-testid="bom-detail">
            <p className={"text-muted text-small mb-2"}>
                <OwLabel title={"bom_components_help"} noBottomMargin={true} />
            </p>

            <div>
                <Typeahead
                    isSelectableFn={isSelectable}
                    typeaheadApiFn={typeaheadCatalogItem}
                    itemsRenderFn={renderLookupItem}
                    titleProp={"sku"}
                    subTitleProp={"name"}
                    confirmAdd={true}
                    typeaheadItems={foundItems}
                    selectedItemFn={selectedItemFn}
                    selectedItemTitleProp={"name"}
                    clearTypeahead={clearTypeahead}
                    clearTypeaheadCallback={clearTypeaheadFn}
                    scrollableList={true}
                    disabled={!!props.disabled}
                    inputTestId="bom-component-typeahead"
                />
            </div>

            <div className={"mt-2"}>
                {lines.length ? (
                    <>
                        <div className={"d-flex"}>
                            <div className={"w-75"}>
                                <OwLabel title={"component"} />
                            </div>
                            <div className={"w-25 text-end"}>
                                <OwLabel title={"qty_per_unit"} />
                            </div>
                        </div>
                        <div className={"bom-detail-rows"}>
                            {lines.map((line, index) => (
                                <div
                                    className={"d-flex align-items-center mt-1 p-2"}
                                    key={line.id ?? `new-${line.component_catalog_item_id}`}
                                    data-testid="bom-line">
                                    <div className={"w-75"}>
                                        <button
                                            type="button"
                                            className={"btn btn-link p-0 me-2 align-baseline"}
                                            disabled={!!props.disabled}
                                            aria-label={intl.formatMessage({ id: "remove", defaultMessage: "Remove" })}
                                            data-testid="bom-line-remove"
                                            onClick={() => removeLine(index)}>
                                            <i
                                                className={"fad fa-trash-alt " + (props.disabled ? "text-muted" : "text-danger")}
                                                aria-hidden="true"
                                            />
                                        </button>
                                        {line.Component?.name}
                                        <span className={"text-muted text-small ms-2"}>{line.Component?.sku}</span>
                                        {renderStatus(line.component_catalog_item_id)}
                                    </div>
                                    <div className={"w-25 d-flex"}>
                                        <div className={"bom-qty-input"}>
                                            <input
                                                className={"form-control form-control-sm text-end"}
                                                name={"qty_per_unit"}
                                                type={"number"}
                                                min={1}
                                                step={1}
                                                inputMode={"numeric"}
                                                aria-label="qty_per_unit"
                                                disabled={!!props.disabled}
                                                value={index in qtyDrafts ? qtyDrafts[index] : line.qty_per_unit}
                                                onChange={(e: ChangeEvent<HTMLInputElement>) => updateQty(index, e)}
                                                onBlur={() => commitQtyDraft(index)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                ) : (
                    <div className={"text-muted"} data-testid="bom-empty">
                        <OwLabel title={"no_components"} noBottomMargin={true} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default BomDetail;
